import { useState } from 'react'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { Spinner } from '@/components/ui/Spinner'
import { useProduits, useCreateProduit, useUpdateProduit, useDeleteProduit } from '@/hooks/useProduits'
import { formatProduitPrice } from '@/types/produit'
import type { Produit, ProduitInsert, ProduitUpdate } from '@/types/database-real'
import { Plus, Edit, Trash2, Package, Search } from 'lucide-react'
import toast from 'react-hot-toast'

interface ProduitModalProps {
  isOpen: boolean
  onClose: () => void
  produit?: Produit | null
}

function ProduitModal({ isOpen, onClose, produit }: ProduitModalProps) {
  const isEditMode = !!produit
  const { createProduit, isCreating } = useCreateProduit()
  const { updateProduit, isUpdating } = useUpdateProduit()

  const [formData, setFormData] = useState({
    nom: produit?.nom || '',
    description: produit?.description || '',
    prix_unitaire: produit?.prix_unitaire?.toString() || '0',
    tva: produit?.tva?.toString() || '20',
    code: produit?.code || '',
    temps_estime: produit?.temps_estime || '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.nom.trim()) {
      toast.error('Le nom est requis')
      return
    }

    const data: ProduitInsert = {
      nom: formData.nom,
      description: formData.description || null,
      prix_unitaire: parseFloat(formData.prix_unitaire) || 0,
      tva: parseFloat(formData.tva) || 20,
      code: formData.code || null,
      temps_estime: formData.temps_estime || null,
    }

    try {
      if (isEditMode && produit) {
        await updateProduit({ id: produit.id, data })
      } else {
        await createProduit(data)
      }
      onClose()
    } catch (error) {
      console.error('Error saving produit:', error)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Modifier le produit' : 'Nouveau produit'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Nom du produit *"
          value={formData.nom}
          onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
          placeholder="Ex: Site web vitrine"
          required
        />

        <div>
          <label className="block text-sm font-medium text-brown-700 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Description détaillée du produit/service..."
            rows={3}
            className="w-full px-4 py-2 bg-white border border-beige-300 rounded-lg text-brown-900 placeholder-brown-400 focus:outline-none focus:ring-2 focus:ring-mint-500"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Prix unitaire (€) *"
            type="number"
            step="0.01"
            min="0"
            value={formData.prix_unitaire}
            onChange={(e) => setFormData({ ...formData, prix_unitaire: e.target.value })}
            placeholder="0.00"
            required
          />

          <Input
            label="TVA (%)"
            type="number"
            step="0.1"
            min="0"
            max="100"
            value={formData.tva}
            onChange={(e) => setFormData({ ...formData, tva: e.target.value })}
            placeholder="20"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Code produit"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            placeholder="Ex: WEB-001"
          />

          <Input
            label="Temps estimé"
            value={formData.temps_estime}
            onChange={(e) => setFormData({ ...formData, temps_estime: e.target.value })}
            placeholder="Ex: 2-3 jours"
          />
        </div>

        <div className="flex gap-3 pt-4 border-t border-beige-200">
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" isLoading={isCreating || isUpdating}>
            {isEditMode ? 'Mettre à jour' : 'Créer'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default function ProductsPage() {
  const [search, setSearch] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingProduit, setEditingProduit] = useState<Produit | null>(null)

  const { produits, isLoading } = useProduits({ search })
  const { deleteProduit, isDeleting } = useDeleteProduit()

  const handleEdit = (produit: Produit) => {
    setEditingProduit(produit)
    setIsModalOpen(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return

    try {
      await deleteProduit(id)
    } catch (error) {
      console.error('Error deleting produit:', error)
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingProduit(null)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-brown-900">
            Produits & Services
          </h1>
          <p className="mt-1 text-brown-600">
            Gérez vos produits et services pré-enregistrés
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingProduit(null)
            setIsModalOpen(true)
          }}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          Nouveau produit
        </Button>
      </div>

      {/* Search */}
      <Card padding="md">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-brown-400" />
          <input
            type="text"
            placeholder="Rechercher un produit..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-beige-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-mint-500"
          />
        </div>
      </Card>

      {/* Products List */}
      <Card padding="none">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : produits.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 mx-auto mb-4 text-beige-300" />
            <p className="text-brown-600 mb-4">
              {search ? 'Aucun produit trouvé' : 'Aucun produit enregistré'}
            </p>
            {!search && (
              <Button
                onClick={() => setIsModalOpen(true)}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                Créer le premier produit
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-beige-100">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-brown-600">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-brown-600">
                    Produit
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-brown-600">
                    Description
                  </th>
                  <th className="px-6 py-3 text-right text-sm font-medium text-brown-600">
                    Prix HT
                  </th>
                  <th className="px-6 py-3 text-center text-sm font-medium text-brown-600">
                    TVA
                  </th>
                  <th className="px-6 py-3 text-center text-sm font-medium text-brown-600">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {produits.map((produit) => (
                  <tr key={produit.id} className="border-t border-beige-100">
                    <td className="px-6 py-4">
                      {produit.code ? (
                        <div className="font-mono font-semibold text-mint-700">
                          {produit.code}
                        </div>
                      ) : (
                        <div className="text-sm text-brown-400">-</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-brown-900">
                        {produit.nom}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-brown-600 max-w-md truncate">
                        {produit.description || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-brown-900">
                      {formatProduitPrice(produit.prix_unitaire)}
                    </td>
                    <td className="px-6 py-4 text-center text-brown-600">
                      {produit.tva}%
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEdit(produit)}
                          className="p-2 text-mint-600 hover:bg-mint-50 rounded-lg transition-colors"
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(produit.id)}
                          disabled={isDeleting}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal */}
      <ProduitModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        produit={editingProduit}
      />
    </div>
  )
}
